import Component from "./component";
import { generateClassName } from "./utils";
import { registerComponent } from "./jquery";
import { mmButton, mmButtonOptions } from "@/types";
import { prefix } from "./constants";
import { decodeHtmlAttributes } from "./utils";

class Button extends Component<mmButtonOptions> implements mmButton {
  private _dxButton!: DevExpress.ui.dxButton;

  constructor(element: HTMLElement, options?: mmButtonOptions) {
    super(element, options);

    this._dxButton = $(this._element)
      .dxButton({
        text: this._options.text,
        icon: this._options.icon,
        rtlEnabled: this._options.rtlEnabled,
        width: this._options.width,
        template: this._options.template,
        useSubmitBehavior: this._options.useSubmitBehavior,
        elementAttr: {
          class: generateClassName(
            "button",
            this._options.color ?? "default",
            this._options.size ?? "md",
            this._options.shape,
            this._options.className,
          ),
  ...(decodeHtmlAttributes(this._options.attributes) ?? {}),
        },
        stylingMode: this._options.style,
        onClick: this._options.onClick,
        disabled: this._options.disabled,
      })
      .dxButton("instance");

    if (this._options.href) {
      const innerHtml = this._dxButton.element().html();
      this._dxButton.element().html(`<a href="${this._options.href}">${innerHtml}</a>`);
    }

    if (this._options.isLoading) {
      this.isLoading = true;
    }
  }

  public getDefaults(): Partial<mmButtonOptions> {
    return {
      color: "default",
      size: "md",
      shape: "normal",
      style: "contained",
    };
  }

  public get color(): "primary" | "secondary" | "default" | "success" | "danger" {
    return this._options.color ?? "default";
  }

  public set color(value: "primary" | "secondary" | "default" | "success" | "danger") {
    const old = this._options.color ?? "default";
    if (old === value) return;

    this._options.color = value;

    const extraAttrClass = (this._options.attributes && (this._options.attributes as any).class) || "";
    const baseClass = generateClassName(
      "button",
      value,
      this._options.size ?? "md",
      this._options.shape,
      this._options.className,
    );
    const finalClass = extraAttrClass ? `${baseClass} ${extraAttrClass}` : baseClass;

    try {
      const currentAttr = this._dxButton.option("elementAttr") || {};
      this._dxButton.option("elementAttr", { ...currentAttr, class: finalClass });
    } catch (err) {
      const $el = $(this._dxButton.element());
      const colors = ["primary", "secondary", "default", "success", "danger"];
      colors.forEach((c) => $el.removeClass(`mm-button-${c}`));
      $el.addClass(`mm-button-${value}`);
    }

    const $el = $(this._dxButton.element());
    const colors = ["primary", "secondary", "default", "success", "danger"];
    colors.forEach((c) => $el.removeClass(`${prefix}-loading-${c}`));
    if ($el.hasClass(`${prefix}-loading`)) {
      $el.addClass(`${prefix}-loading-${value}`);
    }
  }

  public get style(): DevExpress.common.ButtonStyle {
    return this._options.style ?? "contained";
  }

  public set style(value: DevExpress.common.ButtonStyle) {
    if (this._options.style === value) return;

    this._options.style = value;

    try {
      this._dxButton.option("stylingMode", value);
    } catch (err) {
      const $el = $(this._dxButton.element());
      const styles = ["contained", "text", "outlined"];
      styles.forEach((s) => $el.removeClass(`${prefix}-style-${s}`));
      $el.addClass(`${prefix}-style-${value}`);
    }
  }

  public get text(): string {
    return this._dxButton.option("text") ?? "";
  }

  public set text(value: string) {
    this._dxButton.option("text", value);
    this._options.text = value;
  }

  public get icon(): string | undefined {
    return this._dxButton.option("icon");
  }

  public set icon(value: string | undefined) {
    this._dxButton.option("icon", value);
    this._options.icon = value;
  }

  public get disabled(): boolean {
    return !!this._dxButton.option("disabled");
  }

  public set disabled(value: boolean) {
    this._dxButton.option("disabled", value);
    this._options.disabled = value;
  }

  public get isLoading(): boolean {
    return !!this._dxButton.option("disabled");
  }

  public set isLoading(value: boolean) {
    this._dxButton.option("disabled", value);

    const classNames = `${prefix}-loading ${prefix}-loading-${this._options.color ?? "default"} ${prefix}-loading-${this._options.size ?? "md"}`;
    if (value) {
      this._dxButton.element().addClass(classNames);
    } else {
      this._dxButton.element().removeClass(classNames);
    }
  }
}

registerComponent("mmButton", Button);

export default Button;
